import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, CheckCircle2, ExternalLink, Sparkles } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { getJobDetail, updateJob, CANDIDATE_APP_URL } from '../services/adminApi';

function toDateInputValue(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function formatDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

const EMPTY_FORM = {
  title: '', company: '', location: '', jobType: '', experience: '', salary: '',
  startAt: '', endAt: '', isActive: true, description: '', requirements: '',
};

const AdminJobDetailPage = () => {
  const location = useLocation();
  // GetJobDetail, GetJobList'teki "id" alanını (temp_job.id) bekliyor,
  // slug bir sorgu parametresi olarak kabul edilmiyor. URL okunabilirlik
  // için slug taşıyor, gerçek id liste sayfasından navigate state'i ile geliyor.
  const jobId = location.state?.id;
  const [job, setJob] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      setLoadError('İlan detayına yalnızca ilan listesinden ulaşılabilir. Lütfen listeye dönüp tekrar deneyin.');
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await getJobDetail(jobId);
        if (cancelled) return;

        if (!res.ok || res.status === false || !res.data) {
          setLoadError(res.message || 'İlan bulunamadı.');
          return;
        }

        const d = res.data;
        setJob(d);
        setForm({
          title: d.title || '',
          company: d.company || '',
          location: d.location || '',
          jobType: d.jobType || '',
          experience: d.experience || '',
          salary: d.salary || '',
          startAt: toDateInputValue(d.startAt),
          endAt: toDateInputValue(d.endAt),
          isActive: d.isActive ?? true,
          description: d.description || '',
          requirements: d.requirements || '',
        });
      } catch {
        if (!cancelled) setLoadError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [jobId]);

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setSaved(false);
  };

  const setChecked = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.checked }));
    setSaved(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    setSaved(false);
    try {
      const payload = {
        id: job?.id,
        title: form.title || null,
        company: form.company || null,
        location: form.location || null,
        jobType: form.jobType || null,
        experience: form.experience || null,
        salary: form.salary || null,
        startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
        endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
        isActive: form.isActive,
        description: form.description || null,
        requirements: form.requirements || null,
      };
      const res = await updateJob(payload);

      if (!res.ok || res.status === false || res.data === false) {
        setSaveError(res.message || 'İlan güncellenemedi.');
        return;
      }

      setSaved(true);
    } catch {
      setSaveError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="İlan Detayı">
      <Link to="/panel/ilanlar" className="admin-back-link">
        <ArrowLeft size={16} />
        İlanlara Dön
      </Link>

      <div className="admin-panel-card">
        {loading && (
          <div className="admin-placeholder">
            <Loader2 className="spin" />
            <h3>Yükleniyor...</h3>
          </div>
        )}

        {!loading && loadError && (
          <div className="admin-placeholder">
            <AlertCircle />
            <h3>İlan bulunamadı</h3>
            <p>{loadError}</p>
          </div>
        )}

        {!loading && !loadError && job && (
          <>
            <div className="admin-detail-header">
              <div className="admin-detail-header-info">
                <h2>{job.title || 'İsimsiz İlan'}</h2>
                <p className="admin-detail-subtitle">
                  {job.slug && (
                    <a
                      href={`${CANDIDATE_APP_URL}/ilan/${job.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Canlı ilanı görüntüle <ExternalLink size={12} />
                    </a>
                  )}
                  {job.sourceUrl && (
                    <a
                      href={job.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginLeft: job.slug ? 12 : 0 }}
                    >
                      Kaynak ilanı görüntüle{job.source ? ` (${job.source})` : ''} <ExternalLink size={12} />
                    </a>
                  )}
                </p>
              </div>
              {job.isAiRewritten ? (
                <span className="admin-badge" style={{ color: '#6D28D9', background: '#F1EBFD' }}>
                  <Sparkles size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                  AI ile yeniden yazıldı
                </span>
              ) : (
                <span className="admin-badge" style={{ color: '#B45309', background: '#FEF3C7' }}>
                  <AlertCircle size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                  Ham içerik, dikkatli inceleyin
                </span>
              )}
            </div>

            <div className="admin-detail-meta">
              {job.company && <span>{job.company}</span>}
              {job.location && <span>{job.location}</span>}
              {job.startAt && <span>Yayın: {formatDate(job.startAt)}</span>}
              {job.endAt && <span>Son başvuru: {formatDate(job.endAt)}</span>}
            </div>

            {saveError && <div className="admin-error">{saveError}</div>}
            {saved && !saveError && <div className="admin-info"><CheckCircle2 size={14} /> İlan güncellendi.</div>}

            <form onSubmit={handleSave}>
              <div className="admin-detail-section">
                <h4>Temel Bilgiler</h4>
                <div className="admin-form-field">
                  <label htmlFor="job-title">Başlık</label>
                  <input id="job-title" type="text" value={form.title} onChange={setField('title')} />
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label htmlFor="job-company">Şirket</label>
                    <input id="job-company" type="text" value={form.company} onChange={setField('company')} />
                  </div>
                  <div className="admin-form-field">
                    <label htmlFor="job-location">Konum</label>
                    <input id="job-location" type="text" value={form.location} onChange={setField('location')} />
                  </div>
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label htmlFor="job-jobType">Çalışma Şekli</label>
                    <input id="job-jobType" type="text" value={form.jobType} onChange={setField('jobType')} />
                  </div>
                  <div className="admin-form-field">
                    <label htmlFor="job-experience">Deneyim</label>
                    <input id="job-experience" type="text" value={form.experience} onChange={setField('experience')} />
                  </div>
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label htmlFor="job-salary">Maaş</label>
                    <input id="job-salary" type="text" value={form.salary} onChange={setField('salary')} />
                  </div>
                  <div className="admin-form-field admin-form-checkbox">
                    <label htmlFor="job-isActive">
                      <input id="job-isActive" type="checkbox" checked={form.isActive} onChange={setChecked('isActive')} />
                      Aktif
                    </label>
                  </div>
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label htmlFor="job-startAt">Yayın Tarihi</label>
                    <input id="job-startAt" type="date" value={form.startAt} onChange={setField('startAt')} />
                  </div>
                  <div className="admin-form-field">
                    <label htmlFor="job-endAt">Son Başvuru Tarihi</label>
                    <input id="job-endAt" type="date" value={form.endAt} onChange={setField('endAt')} />
                  </div>
                </div>
              </div>

              <div className="admin-detail-section">
                <h4>İlan İçeriği</h4>
                <div className="admin-form-field">
                  <label htmlFor="job-description">Açıklama</label>
                  <textarea id="job-description" rows={6} value={form.description} onChange={setField('description')} />
                </div>
                <div className="admin-form-field">
                  <label htmlFor="job-requirements">Aranan Nitelikler</label>
                  <textarea id="job-requirements" rows={4} value={form.requirements} onChange={setField('requirements')} />
                </div>
              </div>

              <button type="submit" className="admin-submit" disabled={saving}>
                {saving ? <Loader2 size={16} className="spin" /> : null}
                {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>
            </form>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminJobDetailPage;
